import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormWidgetTheme } from "@xapp/stentor-form-widget-channel";

import { FormWidgetConfig } from "../../config";
import { log, err } from "../../utils";
import { SearchStoreProvider, useSearchDispatch, useSearchState } from "../../store";
import { ModalDialog } from "../ModalDialog";
import { FormWidget } from "../FormWidget";
import { WidgetStylesheet } from "../WidgetStylesheet";
import { classNames } from "../../constants/variables";
import SideButton from "../SideButton/SideButton";
import { useAutoOpenTriggers, hasAutoOpenTriggers, DEFAULT_SESSION_KEY } from "../../hooks";
import { isEmbedded } from "../../utils/isEmbedded";
import { takeEarlyClick } from "../../utils/earlyClicks";

import { StentorApi } from "../../api";

import styles from "./Form.module.scss";

export interface FormProps {
    readonly config: FormWidgetConfig;
    readonly theme?: FormWidgetTheme;
    /**
     * Optional elements that will act as the button to enable the modal search.
     */
    readonly button?: JSX.Element | string;
    readonly buttonElementLookup?: () => Promise<HTMLElement>;
}

/**
 * FormContainer is the main entry point for the Form widget. It provides the context for the
 * form and handles the button click events.
 * It is a wrapper around the Form component.
 */
export const FormContainer: FC<FormProps> = (props) => {
    return (
        <SearchStoreProvider>
            <Form
                config={props.config}
                theme={props.theme}
                button={props.button}
                buttonElementLookup={props.buttonElementLookup}
            />
        </SearchStoreProvider>
    );
};

/**
 * Form is the main component for the Form widget. It provides the context for the
 * form and handles the button click events.
 */
/**
 * Options that can be passed when programmatically opening the form
 */
export interface OpenFormOptions {
    /**
     * Label of a chip to auto-select after the form loads.
     * If the first step of the form contains chips, the form will
     * automatically click the chip matching this label.
     */
    preselectChipLabel?: string;
}

export const Form: FC<FormProps> = (props) => {
    const { config, theme } = props;

    const dispatch = useSearchDispatch();

    const { active, minimized } = useSearchState();

    // Store preselectChipLabel when form is opened programmatically
    const [preselectChipLabel, setPreselectChipLabel] = useState<string | undefined>();

    // Track if user has interacted with the form (for auto-open triggers)
    const [hasInteracted, setHasInteracted] = useState(false);

    // Track if the form was opened by an auto-trigger (vs manually)
    const [openedByTrigger, setOpenedByTrigger] = useState(false);

    // The host page opts into inline rendering by pre-creating `#xapp-form-widget` with the
    // `embedded` class and sizing it itself; App.tsx's renderWidget then renders into that
    // div rather than appending a floating one.
    //
    // Read once at mount, not per render: renderWidget guarantees the element exists before
    // React mounts into it, so there is nothing to wait for, and re-querying the document on
    // every render would do it on every keystroke. It also pins the layout for the session --
    // a host page whose CSS framework or A/B tool churns classes cannot flip the widget
    // between an inline form and a full-screen modal mid-conversation.
    const [embedded] = useState(isEmbedded);

    // `autoOpen.enabled` with no behavior-based triggers configured means "open
    // immediately on load" (docked, no close button). If triggers ARE configured,
    // `enabled` only turns the feature on -- the widget must wait for a trigger
    // to fire (which sets `active` via handleAutoOpen) rather than bypassing it.
    const immediateAutoOpen = !!config.autoOpen?.enabled && !hasAutoOpenTriggers(config.autoOpen);

    // Open-only, never a toggle: a host page can wire its own buttons to our side button while
    // Studio also binds them via triggerSelector (squeaksservices.com did both), and a <button>
    // gets focus and click from one mouse press. Toggles from any two of those cancel out.
    const openModal = useCallback(
        (options?: OpenFormOptions) => {
            // Store the preselect label if provided
            if (options?.preselectChipLabel) {
                setPreselectChipLabel(options.preselectChipLabel);
            }
            dispatch({
                type: "open-modal-state",
            });
        },
        [dispatch]
    );

    // Event handler for DOM events (onclick, onfocus)
    const handleFocus = useCallback(() => {
        openModal();
    }, [openModal]);

    // API function that accepts options (for window.xafwControl.openForm)
    const openFormWithOptions = useCallback(
        (options?: OpenFormOptions) => {
            openModal(options);
        },
        [openModal]
    );

    const formStatus = useCallback(() => {
        return { active, minimized };
    }, [active, minimized]);

    const api = useMemo(() => {
        return StentorApi.getApi({
            key: config.api.key,
            url: config.api.url,
            // Must match the config FormWidget passes below: getApi caches per url+key+intentId, so
            // this and FormWidget's call share one instance only if their intentId agrees. Form also
            // sends FORM_CLOSE from here, which needs the right intentId for overrideKey.
            intentId: config.intentId,
            // Carried through so the preview guarantee lives in one place.
            demoMode: config.api.demoMode,
            // prefetchQuery: config.autoGreeting
        });
    }, [config]);

    const handleClose = useCallback(() => {
        // Clear preselectChipLabel when form closes
        setPreselectChipLabel(undefined);
        // Mark as interacted to prevent auto-open triggers from firing again
        setHasInteracted(true);
        // Only persist dismissal if the form was opened by an auto-trigger
        // This prevents manual opens from blocking future auto-triggers
        if (openedByTrigger) {
            try {
                const sessionKey = config.autoOpen?.sessionKey || DEFAULT_SESSION_KEY;
                sessionStorage.setItem(sessionKey, "true");
            } catch {
                // Private browsing or sessionStorage not available - ignore
            }
            setOpenedByTrigger(false);
        }
        dispatch({
            type: "new-search",
        });
        dispatch({
            type: "set-modal-state",
            active: false,
            minimized: false,
            sendRequest: false,
        });

        api.channelActionRequest({ data: { result: {}, step: "", form: "" } }, "FORM_CLOSE").then(
            (response) => {
                log(`ACTION Request (FORM_CLOSE). Data: Response: ${JSON.stringify(response)}`);
            }
        );
    }, [api, config.autoOpen?.sessionKey, dispatch, openedByTrigger]);

    // Callback for auto-open triggers
    const handleAutoOpen = useCallback(() => {
        setOpenedByTrigger(true);
        dispatch({
            type: "set-modal-state",
            active: true,
            minimized: false,
            sendRequest: true,
        });
    }, [dispatch]);

    // Set up auto-open triggers (delay, scroll, exit intent, inactivity)
    useAutoOpenTriggers({
        config: config.autoOpen,
        isActive: active,
        onOpen: handleAutoOpen,
        hasInteracted,
    });

    // This sets up the button
    useEffect(() => {
        const elements = document.getElementsByClassName(
            classNames.XAPP_FORM_BUTTON
        ) as HTMLCollectionOf<HTMLElement>;

        // we found the button by class
        if (elements.length) {
            log(`Assigning ${elements.length} button(s) by class: ${classNames.XAPP_FORM_BUTTON}`);

            Array.from(elements).forEach((element) => {
                element.onclick = handleFocus;
                element.onfocus = handleFocus;
            });
        } else {
            // we didn't find the button by class, let's try by id
            const el = document.getElementById(classNames.XAPP_FORM_BUTTON);
            if (el) {
                log(`Assigning one button by id: ${classNames.XAPP_FORM_BUTTON})}`);
                el.onclick = handleFocus;
                el.onfocus = handleFocus;
            } else {
                log(`Cannot assign buttons by class or id: ${classNames.XAPP_FORM_BUTTON})}`);
            }
        }

        window.xafwControl = { openForm: openFormWithOptions, formStatus };
    }, [formStatus, handleFocus, openFormWithOptions]);

    // Opens the form from existing elements on the customer's site matched by
    // config.triggerSelector (e.g. a dead CTA left over from a prior integration), so the widget
    // can be installed without the customer editing anything. Additive to (never replaces) the
    // xapp-form-button binding above.
    //
    // One capture-phase listener on the document rather than one per element: it covers
    // elements the page adds later, clicks on an element's children, and host handlers that
    // stop propagation, with nothing to watch or time out.
    useEffect(() => {
        const triggerSelector = config.triggerSelector;
        // Taken whether or not a selector is configured, which also stops the recorder.
        const earlyClick = takeEarlyClick();
        if (!triggerSelector) {
            return undefined;
        }

        try {
            document.querySelector(triggerSelector);
        } catch (e) {
            // An invalid selector can never become valid -- log once rather than on every click.
            err(`Invalid triggerSelector "${triggerSelector}": ${e}`);
            return undefined;
        }

        if (earlyClick?.closest(triggerSelector)) {
            log(`Opening for a triggerSelector "${triggerSelector}" click made before the form loaded`);
            handleFocus();
        }

        const onClick = (event: MouseEvent) => {
            const target = event.target instanceof Element ? event.target.closest(triggerSelector) : null;
            if (!target) {
                return;
            }
            // Only suppress navigation for a literal dead link (the common leftover-CTA
            // case) -- never stopPropagation, never overwrite an existing listener/onclick.
            if (
                target instanceof HTMLAnchorElement &&
                (target.getAttribute("href") === "#" || target.getAttribute("href") === "")
            ) {
                event.preventDefault();
            }
            handleFocus();
        };

        document.addEventListener("click", onClick, true);

        return () => {
            document.removeEventListener("click", onClick, true);
        };
    }, [config.triggerSelector, handleFocus]);

    let button: JSX.Element;

    // if the passed in a button, use it
    if (props.button) {
        if (typeof props.button === "object") {
            // If they provided a button, we add the onClick
            button = (
                <props.button.type
                    {...props.button.props}
                    onClick={handleFocus}
                    onFocus={handleFocus}
                />
            );
        } else {
            // We are using an ID for the button, just put in a placeholder
            button = <></>;
        }
    } else {
        // no button prop provided so use default search container
        button = <></>;
    }

    return (
        <div className={styles.xapp_form}>
            <WidgetStylesheet theme={theme} />

            {/*
                Hidden when embedded: the side button is a `position: fixed` page-edge tab
                that opens the floating modal, so it would both escape the customer's
                container and offer a second way into a form already on the page.
            */}
            {config.sideButtonLabel && !immediateAutoOpen && !embedded && (
                <SideButton
                    onClick={handleFocus}
                    label={config.sideButtonLabel}
                    position={theme?.sideButton?.position}
                    hidden={config.hideSideButton}
                />
            )}

            {button}

            {(active || immediateAutoOpen) && (
                <ModalDialog
                    disableBackdrop={immediateAutoOpen}
                    embedded={embedded}
                    onClose={handleClose}>
                    <FormWidget
                        docked={immediateAutoOpen}
                        embedded={embedded}
                        openedByTrigger={openedByTrigger}
                        config={config}
                        preselectChipLabel={preselectChipLabel}
                        onPreselectChipUsed={() => setPreselectChipLabel(undefined)}
                    />
                </ModalDialog>
            )}
        </div>
    );
};
